import { EntityMeta, SystemEntity } from '@backstage/catalog-model';
import { useApp } from '@backstage/core-plugin-api';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import { Box, Card, CardContent, CardHeader, createStyles, Divider, Link, makeStyles, Theme, Typography } from '@material-ui/core';
import React from 'react';
import { DevopsPipelinesIcon, DevopsTestLabIcon, GitIcon } from '../icons';
import { DevopsMetadataLinks, useDevopsMetaLinks } from './devops/DevopsTypeMetadataReader';

interface LinkProps {
  href: string,
  label: string,
  icon: React.ReactElement
  action?: () => void;
}

const useIconLinkStyles = makeStyles(createStyles({
  link: {
    display: 'grid',
    justifyItems: 'center',
    textAlign: 'center'
  },
  text: {
    textTransform: 'uppercase',
    letterSpacing: 0.75
  }
}));

const IconLink = (props: { options: LinkProps }) => {
  const { label, href, icon, action } = props.options;
  const classes = useIconLinkStyles();
  if (!href && action) {
    return (
      <Link title={label} onClick={action} className={classes.link} style={{ cursor: 'pointer' }}>
        {icon}
        <Typography variant='caption' component='span' className={classes.text}>
          {label}
        </Typography>
      </Link>
    );
    
  }

  return (
    <Link title={label} href={href} rel='noopener' target='_blank' className={classes.link}>
      {icon}
      <Typography variant='caption' component='span' className={classes.text}>
        {label}
      </Typography>
    </Link>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    divider: {
      marginTop: '1em'
    },
    linksList: {
      display: 'grid',
      justifyContent: 'center',
      gap: theme.spacing(2),
      paddingTop: 1,
      gridTemplateColumns: `repeat(auto-fit, minmax(auto, ${theme.spacing(10)}px))`,
    }
  })
);

export const EntityLinkCard = (props: { entity: SystemEntity }) => {

  const { metadata } = props.entity;

  const classes = useStyles();
  const links: LinkProps[] = [];

  useDevopsLinks(links, metadata);
  useGenericLinks(links, metadata);

  return (<Card>
    <CardHeader title="Links" />
    <Divider className={classes.divider} />
    <CardContent>

      {links?.length ? (
        <Box className={classes.linksList} >
          {links.map((link, index) => <IconLink key={index} options={link} />)}
        </Box>
      ) : (
        <Typography>The entity has no links.</Typography>
      )}
    </CardContent>
  </Card>);
}

function useGenericLinks(links: LinkProps[], metadata: EntityMeta) {
  const app = useApp();
  metadata.links?.forEach(link => {
    const SysIcon = app.getSystemIcon(link.icon || '');
    const icon = SysIcon ? <SysIcon /> : <OpenInNewIcon />;
    const label = link.title || link.icon || '';
    links.push({ href: link.url, icon, label });
  });
}

function useDevopsLinks(links: LinkProps[], metadata: EntityMeta) {

  const devops: DevopsMetadataLinks = useDevopsMetaLinks(metadata);

  if (devops.repository.length) {
    links.push({ href: devops.repository, icon: <GitIcon />, label: 'Source' });
  }

  if (devops.pipelines.length) {
    links.push({ href: devops.pipelines, icon: <DevopsPipelinesIcon />, label: 'Pipelines' });
  }

  if (devops.pipelinesAutomatedTests.length) {
    links.push({ href: devops.pipelinesAutomatedTests, icon: <DevopsTestLabIcon />, label: 'Tests' });
  }
}

